import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useRecentlyViewed } from "@/hooks/useRecentlyViewed";
import { useShopifyProducts } from "@/context/ShopifyProductsContext";
import { useCompare } from "@/context/CompareContext";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import BackInStockModal from "@/pages/shop/detail/components/BackInStockModal";
import type { Product } from "@/types/product";

interface MiniCardProps {
  product: Product;
  onNavigate: () => void;
}

function MiniCard({ product, onNavigate }: MiniCardProps) {
  const navigate = useNavigate();
  const { toggleCompare, isInCompare, canAdd } = useCompare();
  const { addItem } = useCart();
  const { isWishlisted, toggleWishlist, openWishlist } = useWishlist();
  const [compareFlash, setCompareFlash] = useState(false);
  const [wishFlash, setWishFlash] = useState(false);
  const [cartFlash, setCartFlash] = useState(false);
  const [cartAdded, setCartAdded] = useState(false);
  const [showQtyPicker, setShowQtyPicker] = useState(false);
  const [qty, setQty] = useState(1);
  const [showBisModal, setShowBisModal] = useState(false);
  const [bisFlash, setBisFlash] = useState(false);
  const qtyPickerRef = useRef<HTMLDivElement>(null);

  const inCompare = isInCompare(product.id);
  const compareDisabled = !inCompare && !canAdd;

  // Close qty picker on outside click
  useEffect(() => {
    if (!showQtyPicker) return;
    const handler = (e: MouseEvent) => {
      if (qtyPickerRef.current && !qtyPickerRef.current.contains(e.target as Node)) {
        setShowQtyPicker(false);
        setQty(1);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showQtyPicker]);

  const handleNavigate = () => {
    navigate(`/shop/${product.id}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
    onNavigate();
  };

  const handleCompare = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (compareDisabled) return;
    toggleCompare(product);
    if (!inCompare) {
      setCompareFlash(true);
      setTimeout(() => setCompareFlash(false), 600);
    }
  };

  const inWishlist = isWishlisted(product.id);

  const handleWishlist = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleWishlist({
      id: product.id,
      variantId: product.variantId,
      name: product.name,
      brand: product.brand,
      price: product.price,
      originalPrice: product.originalPrice,
      image: product.image,
      inStock: product.inStock,
      category: product.category,
    });
    if (!inWishlist) {
      setWishFlash(true);
      setTimeout(() => setWishFlash(false), 600);
      openWishlist();
    }
  };

  const handleCartIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!product.inStock || cartAdded) return;
    setShowQtyPicker((v) => {
      if (!v) setQty(1);
      return !v;
    });
  };

  const handleConfirmAdd = (e: React.MouseEvent) => {
    e.stopPropagation();
    addItem(
      {
        id: product.id,
        variantId: product.variantId,
        name: product.name,
        brand: product.brand,
        price: product.price,
        image: product.image,
      },
      qty,
    );
    setShowQtyPicker(false);
    setQty(1);
    setCartFlash(true);
    setCartAdded(true);
    setTimeout(() => setCartFlash(false), 600);
    setTimeout(() => setCartAdded(false), 2200);
  };

  const savePct = product.originalPrice
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : null;

  return (
    <div className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-amber-50 transition-colors border border-transparent hover:border-amber-200 group">
      {/* Clickable area: thumbnail + info */}
      <button
        onClick={handleNavigate}
        className="flex items-center gap-2.5 flex-1 min-w-0 text-left cursor-pointer"
      >
        {/* Thumbnail */}
        <div className="w-12 h-12 flex items-center justify-center shrink-0 bg-gray-50 rounded-lg overflow-hidden border border-gray-100">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-contain p-1 group-hover:scale-105 transition-transform duration-200"
          />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide truncate leading-tight">
            {product.brand}
          </p>
          <p className="text-xs font-semibold text-gray-800 leading-snug line-clamp-2 mt-0.5 group-hover:text-amber-700 transition-colors">
            {product.name}
          </p>

          {/* Star rating row */}
          <div className="flex items-center gap-1 mt-0.5 mb-0.5">
            <div className="flex items-center gap-px">
              {Array.from({ length: 5 }, (_, i) => {
                const full = product.rating >= i + 0.8;
                const half = !full && product.rating >= i + 0.3;
                return (
                  <i
                    key={i}
                    className={`text-[9px] ${
                      full
                        ? "ri-star-fill text-amber-400"
                        : half
                        ? "ri-star-half-line text-amber-400"
                        : "ri-star-line text-gray-300"
                    }`}
                  ></i>
                );
              })}
            </div>
            <span className="text-[9px] font-bold text-amber-600 leading-none tabular-nums">
              {product.rating.toFixed(1)}
            </span>
            <span className="text-[9px] text-gray-400 leading-none">
              ({product.reviews >= 1000
                ? `${(product.reviews / 1000).toFixed(1)}k`
                : product.reviews})
            </span>
          </div>

          <div className="flex items-center gap-1.5 mt-0.5">
            <span className={`text-xs font-extrabold ${product.originalPrice ? "text-red-600" : "text-gray-900"}`}>
              ${product.price.toLocaleString()}
            </span>
            {savePct && (
              <span className="text-[9px] font-bold bg-red-100 text-red-600 px-1 py-0.5 rounded whitespace-nowrap">
                -{savePct}%
              </span>
            )}
            {!product.inStock && (
              <span className="text-[9px] font-bold bg-gray-100 text-gray-400 px-1 py-0.5 rounded whitespace-nowrap">
                Sold Out
              </span>
            )}
          </div>
        </div>
      </button>

      {/* Right action column */}
      <div className="flex flex-col items-center gap-1 shrink-0">
        {/* Compare button */}
        <div className="relative group/cmp">
          <button
            onClick={handleCompare}
            disabled={compareDisabled}
            className={`w-6 h-6 flex items-center justify-center rounded-md transition-all duration-200 cursor-pointer
              ${compareFlash ? "scale-125" : "scale-100"}
              ${inCompare
                ? "bg-amber-500 text-white"
                : compareDisabled
                ? "bg-gray-100 text-gray-300 cursor-not-allowed"
                : "bg-gray-100 text-gray-400 hover:bg-amber-100 hover:text-amber-600"
              }`}
          >
            <i className="ri-scales-3-line text-xs"></i>
          </button>
          <div className="absolute right-full mr-1.5 top-1/2 -translate-y-1/2 pointer-events-none opacity-0 group-hover/cmp:opacity-100 transition-opacity duration-150 z-10">
            <div className="bg-gray-900 text-white text-[9px] font-semibold px-2 py-1 rounded whitespace-nowrap">
              {inCompare ? "In compare" : compareDisabled ? "List full" : "Compare"}
            </div>
          </div>
        </div>

        {/* Wishlist button */}
        <div className="relative group/wish">
          <button
            onClick={handleWishlist}
            className={`w-6 h-6 flex items-center justify-center rounded-md transition-all duration-200 cursor-pointer
              ${wishFlash ? "scale-125" : "scale-100"}
              ${inWishlist
                ? "bg-red-100 text-red-500"
                : "bg-gray-100 text-gray-400 hover:bg-red-50 hover:text-red-400"
              }`}
          >
            <i className={`text-xs ${inWishlist ? "ri-heart-fill" : "ri-heart-line"}`}></i>
          </button>
          <div className="absolute right-full mr-1.5 top-1/2 -translate-y-1/2 pointer-events-none opacity-0 group-hover/wish:opacity-100 transition-opacity duration-150 z-10">
            <div className="bg-gray-900 text-white text-[9px] font-semibold px-2 py-1 rounded whitespace-nowrap">
              {inWishlist ? "Saved" : "Save"}
            </div>
          </div>
        </div>

        {/* Cart button + qty stepper  OR  Back-in-stock bell */}
        {product.inStock ? (
          <div ref={qtyPickerRef} className="relative">
            {/* Qty picker popover — slides left from the button */}
            {showQtyPicker && (
              <div
                className="absolute right-8 top-1/2 -translate-y-1/2 z-30 flex items-center gap-1 bg-white border border-green-300 rounded-xl shadow-xl px-2 py-1.5"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Minus */}
                <button
                  onClick={(e) => { e.stopPropagation(); setQty((q) => Math.max(1, q - 1)); }}
                  className="w-5 h-5 flex items-center justify-center rounded-md bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors cursor-pointer"
                >
                  <i className="ri-subtract-line text-[10px] font-bold"></i>
                </button>

                {/* Qty display */}
                <span className="text-xs font-extrabold text-gray-900 w-5 text-center tabular-nums select-none">
                  {qty}
                </span>

                {/* Plus */}
                <button
                  onClick={(e) => { e.stopPropagation(); setQty((q) => Math.min(99, q + 1)); }}
                  className="w-5 h-5 flex items-center justify-center rounded-md bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors cursor-pointer"
                >
                  <i className="ri-add-line text-[10px] font-bold"></i>
                </button>

                {/* Divider */}
                <span className="w-px h-3.5 bg-gray-200 mx-0.5 shrink-0"></span>

                {/* Confirm add */}
                <button
                  onClick={handleConfirmAdd}
                  className="h-5 px-2.5 flex items-center justify-center rounded-lg bg-green-500 hover:bg-green-600 active:scale-95 text-white text-[9px] font-extrabold tracking-wide transition-all duration-150 cursor-pointer whitespace-nowrap"
                >
                  Add
                </button>

                {/* Cancel */}
                <button
                  onClick={(e) => { e.stopPropagation(); setShowQtyPicker(false); setQty(1); }}
                  className="w-5 h-5 flex items-center justify-center rounded-md text-gray-300 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-[10px]"></i>
                </button>
              </div>
            )}

            {/* Cart icon button */}
            <button
              onClick={handleCartIconClick}
              title={cartAdded ? "Added to cart!" : showQtyPicker ? "Close picker" : "Add to cart"}
              className={`w-6 h-6 flex items-center justify-center rounded-md transition-all duration-200 cursor-pointer
                ${cartFlash ? "scale-125" : "scale-100"}
                ${cartAdded
                  ? "bg-green-500 text-white"
                  : showQtyPicker
                  ? "bg-green-100 text-green-600 ring-1 ring-green-400"
                  : "bg-gray-100 text-gray-400 hover:bg-green-100 hover:text-green-600"
                }`}
            >
              <i className={`text-xs ${cartAdded ? "ri-check-line" : "ri-shopping-cart-2-line"}`}></i>
            </button>
          </div>
        ) : (
          /* Bell button — only for out-of-stock products */
          <div className="relative group/bell">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setBisFlash(true);
                setTimeout(() => setBisFlash(false), 600);
                setShowBisModal(true);
              }}
              title="Notify me when back in stock"
              className={`w-6 h-6 flex items-center justify-center rounded-md transition-all duration-200 cursor-pointer
                ${bisFlash ? "scale-125" : "scale-100"}
                bg-amber-50 text-amber-500 hover:bg-amber-100 hover:text-amber-600`}
            >
              <i className="ri-notification-3-line text-xs"></i>
            </button>
            <div className="absolute right-full mr-1.5 top-1/2 -translate-y-1/2 pointer-events-none opacity-0 group-hover/bell:opacity-100 transition-opacity duration-150 z-10">
              <div className="bg-gray-900 text-white text-[9px] font-semibold px-2 py-1 rounded whitespace-nowrap">
                Notify me
              </div>
            </div>
          </div>
        )}

        {/* Navigate arrow */}
        <button
          onClick={handleNavigate}
          className="w-6 h-6 flex items-center justify-center text-gray-300 hover:text-amber-500 transition-colors cursor-pointer rounded-md hover:bg-amber-50"
        >
          <i className="ri-arrow-right-s-line text-base"></i>
        </button>
      </div>

      {/* Back-in-stock modal — portals outside the strip's stacking context */}
      <BackInStockModal
        isOpen={showBisModal}
        onClose={() => setShowBisModal(false)}
        productName={product.name}
        productId={product.id}
      />
    </div>
  );
}

export default function RecentlyViewedStrip() {
  const [open, setOpen] = useState(false);
  const recentIds = useRecentlyViewed();
  const { products } = useShopifyProducts();
  const panelRef = useRef<HTMLDivElement>(null);

  const recentProducts = recentIds
    .map((id) => products.find((p) => p.id === id))
    .filter((p): p is Product => !!p)
    .slice(0, 8);

  // Don't render at all if no history yet
  if (recentProducts.length === 0) return null;

  return (
    <>
      {/* Backdrop (mobile) */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/20 md:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Floating container */}
      <div className="fixed right-0 top-1/2 -translate-y-1/2 z-50 flex items-stretch">

        {/* Collapsed tab — always visible */}
        <button
          onClick={() => setOpen((v) => !v)}
          className={`flex flex-col items-center justify-center gap-1.5 w-9 py-5 rounded-l-xl shadow-md cursor-pointer transition-all duration-300 whitespace-nowrap ${
            open
              ? "bg-amber-500 text-white"
              : "bg-gray-900 text-white hover:bg-amber-500"
          }`}
          aria-label={open ? "Close recently viewed" : "Show recently viewed"}
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-history-line text-base"></i>
          </div>
          {!open && (
            <span className="w-5 h-5 flex items-center justify-center bg-amber-500 rounded-full text-[9px] font-extrabold text-white shrink-0">
              {recentProducts.length}
            </span>
          )}
          <span
            className="text-[9px] font-semibold tracking-widest uppercase leading-none"
            style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
          >
            {open ? "Close" : "Recent"}
          </span>
        </button>

        {/* Expanded panel */}
        <div
          ref={panelRef}
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            open ? "w-72 opacity-100" : "w-0 opacity-0"
          }`}
        >
          <div className="w-72 h-full bg-white border-l border-t border-b border-gray-200 rounded-l-xl shadow-xl flex flex-col"
            style={{ maxHeight: "calc(100vh - 120px)" }}
          >
            {/* Header */}
            <div className="px-4 pt-4 pb-3 border-b border-gray-100 shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 flex items-center justify-center bg-amber-100 rounded-lg">
                    <i className="ri-history-line text-amber-600 text-sm"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-extrabold text-gray-900 leading-tight">Recently Viewed</h3>
                    <p className="text-[10px] text-gray-400 leading-tight">
                      {recentProducts.length} product{recentProducts.length !== 1 ? "s" : ""} from your session
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-base"></i>
                </button>
              </div>
            </div>

            {/* Scrollable product list */}
            <div className="flex-1 overflow-y-auto p-2" style={{ scrollbarWidth: "thin", scrollbarColor: "#e5e7eb transparent" }}>
              <div className="space-y-0.5">
                {recentProducts.map((p) => (
                  <MiniCard key={p.id} product={p} onNavigate={() => setOpen(false)} />
                ))}
              </div>
            </div>

            {/* Footer hint */}
            <div className="px-4 py-3 border-t border-gray-100 shrink-0">
              <div className="flex items-center justify-center flex-wrap gap-x-2 gap-y-1 text-[9px] text-gray-400">
                <span className="flex items-center gap-0.5">
                  <i className="ri-shopping-cart-2-line text-green-500"></i>
                  Add
                </span>
                <span className="w-px h-3 bg-gray-200"></span>
                <span className="flex items-center gap-0.5">
                  <i className="ri-notification-3-line text-amber-500"></i>
                  Notify
                </span>
                <span className="w-px h-3 bg-gray-200"></span>
                <span className="flex items-center gap-0.5">
                  <i className="ri-heart-line text-red-400"></i>
                  Save
                </span>
                <span className="w-px h-3 bg-gray-200"></span>
                <span className="flex items-center gap-0.5">
                  <i className="ri-scales-3-line text-amber-500"></i>
                  Compare
                </span>
                <span className="w-px h-3 bg-gray-200"></span>
                <span className="flex items-center gap-0.5">
                  <i className="ri-arrow-right-s-line text-amber-500"></i>
                  View
                </span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </>
  );
}
